import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  MoreHorizontal,
  Plus,
  Redo2,
  Search,
  SlidersHorizontal,
  Undo2,
} from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useProductWorkbench } from '@/hooks/useProductWorkbench';
import { toast } from '@/lib/toast';
import { getHubSpotContext } from '@/lib/hubspotContext';
import productApi from '@/lib/productApi';
import { ErrorReportButton } from '@/components/ErrorReportPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import WorkbenchProductRow from '@/components/products/WorkbenchProductRow';
import WorkbenchRatesPanel from '@/components/products/WorkbenchRatesPanel';
import RatesDialog from '@/components/products/RatesDialog';
import RateRemovalDialog from '@/components/products/RateRemovalDialog';
import PublishDialog from '@/components/products/PublishDialog';
import type { RateView } from '@/types/product';
import ReviewerGroupsDialog from '@/components/products/ReviewerGroupsDialog';
import { matchesProduct, hours, money } from '@/components/products/workbenchPricing';
import ProductManagementPage from './ProductManagementPage';

// Radix Select staat geen lege waarde toe; '' in de state betekent "alle categorieën".
const ALL_CATEGORIES = '__all__';

export default function ProductsPage() {
  usePageTitle('Producten');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const work = useProductWorkbench();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [ratesOpen, setRatesOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      return Math.max(
        304,
        Math.min(640, Number(localStorage.getItem('products.rates-sidebar.width')) || 304)
      );
    } catch {
      return 304;
    }
  });
  const resizeSidebar = (width: number) => {
    const next = Math.max(304, Math.min(640, Math.round(width)));
    setSidebarWidth(next);
    try {
      localStorage.setItem('products.rates-sidebar.width', String(next));
    } catch {
      /* The width remains usable in memory. */
    }
  };
  const [management, setManagement] = useState(false);
  const [ratesDialog, setRatesDialog] = useState(false);
  const [removingRate, setRemovingRate] = useState<RateView | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [revealId, setRevealId] = useState<string | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(params.get('expand') ? [params.get('expand')!] : [])
  );
  const deepLinkScrolled = useRef(false);
  useEffect(() => {
    const id = params.get('expand');
    if (!id || work.loading || deepLinkScrolled.current) return;
    const priceBuild = document.getElementById(`price-build-${id}`);
    if (priceBuild) {
      priceBuild.scrollIntoView({ block: 'center' });
      deepLinkScrolled.current = true;
    }
  }, [params, work.loading]);
  // Een kopie verschijnt na het herladen; open en focus hem, zodat je meteen verder kunt.
  useEffect(() => {
    if (!revealId) return;
    document.getElementById(`price-build-${revealId}`)?.scrollIntoView({ block: 'center' });
    document
      .querySelector<HTMLButtonElement>(`[aria-controls="price-build-${revealId}"]`)
      ?.focus({ preventScroll: true });
    setRevealId(null);
  }, [revealId]);
  const [selectedCell, setSelectedCell] = useState<{
    productId: string;
    lineId: string;
    variantId: string;
  } | null>(null);
  const visible = work.products.filter(
    (product) =>
      (!category || product.category === category) && matchesProduct(product, product.spec, search)
  );
  const categories = [
    ...new Set(
      work.products
        .map((product) => product.category)
        .filter((value): value is string => Boolean(value))
    ),
  ];
  const groups = useMemo(() => {
    const map = new Map<string, typeof visible>();
    for (const product of visible) {
      const name = product.category || 'Overig';
      map.set(name, [...(map.get(name) ?? []), product]);
    }
    return [...map.entries()];
  }, [visible]);
  const rateCount = Object.keys(work.drafts.rates).length;
  const publishingProduct = work.products.find((product) => product.id === publishingId);
  const duplicatingProduct = work.products.find((product) => product.id === duplicatingId);
  const productCount = Object.keys(work.drafts.products).length;
  const affected = work.products
    .filter((product) => product.changed)
    .map((product) => product.name);
  const selectedProduct = work.products.find((product) => product.id === selectedCell?.productId);
  const selectedLine = selectedProduct?.spec.lines.find((line) => line.id === selectedCell?.lineId);
  const selectedVariant = selectedProduct?.spec.variants.find(
    (variant) => variant.id === selectedCell?.variantId
  );
  const selectedComputation = selectedProduct?.computation.variants.find(
    (variant) => variant.variantId === selectedCell?.variantId
  );
  const quotesHref = useMemo(() => {
    const context = getHubSpotContext();
    const query = new URLSearchParams();
    if (context.portalId) query.set('portalId', context.portalId);
    if (context.dealId) query.set('dealId', context.dealId);
    return `/quotes${query.size ? `?${query}` : ''}`;
  }, []);
  const openDetail = async (id: string) => {
    if (await work.prepareDetail(id)) navigate(`/products/${id}`);
  };
  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const product = await productApi.create({ name: newName.trim() });
      navigate(`/products/${product.id}`);
    } catch (error) {
      toast.error('Product aanmaken is niet gelukt', { error });
    } finally {
      setCreating(false);
    }
  };
  const duplicate = async () => {
    if (!duplicatingProduct || !duplicateName.trim()) return;
    setCreating(true);
    try {
      const copy = await productApi.duplicate(duplicatingProduct.id, {
        name: duplicateName.trim(),
        spec: duplicatingProduct.spec,
        billingPeriod: duplicatingProduct.billingPeriod,
      });
      setDuplicatingId(null);
      await work.reload();
      setExpanded((current) => new Set(current).add(copy.id));
      setRevealId(copy.id);
      toast.success(`${copy.name} staat als concept klaar`);
    } catch (error) {
      toast.error('Dupliceren is niet gelukt', { error });
    } finally {
      setCreating(false);
    }
  };
  if (management)
    return (
      <>
        <div className="mx-auto max-w-[1400px] px-6 pt-5">
          <Button
            variant="outline"
            onClick={() => {
              setManagement(false);
              void work.reload();
            }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Terug naar productwerkblad
          </Button>
        </div>
        <ProductManagementPage />
      </>
    );

  return (
    <div
      className="product-workbench mx-auto max-w-[1440px] px-4 pb-6 pt-5 sm:px-8"
      aria-busy={work.busy}
    >
      <header className="flex flex-wrap items-end justify-between gap-4 border-b pb-5">
        <div>
          <button
            type="button"
            onClick={() => navigate(quotesHref)}
            className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Terug naar offertes
          </button>
          <h1 className="text-2xl font-bold">Producten</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Bekijk producten en pas uren, prijzen en tarieven aan.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={ratesOpen ? 'secondary' : 'default'}
            disabled={work.busy}
            aria-pressed={ratesOpen}
            onClick={() => setRatesOpen(!ratesOpen)}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            Tarieven wijzigen
          </Button>
          <Button variant="outline" onClick={() => setNewOpen(true)} disabled={work.busy}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuw product
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Beheer" disabled={work.busy}>
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setManagement(true)}>
                Catalogus beheren
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setRatesDialog(true)}>
                Tariefbeheer en historie
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setGroupsOpen(true)}>Keuzegroepen</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void work.reload()}>
                Actuele gegevens laden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <div className="flex flex-wrap items-center justify-between gap-3 py-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-80 max-w-full">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              aria-label="Producten zoeken"
              placeholder="Zoek product of werkzaamheid"
              className="pl-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <Select
            value={category || ALL_CATEGORIES}
            onValueChange={(value) => setCategory(value === ALL_CATEGORIES ? '' : value)}
          >
            <SelectTrigger aria-label="Categorie" className="w-52 max-w-full bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CATEGORIES}>Alle categorieën</SelectItem>
              {categories.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-xs text-muted-foreground">
          {visible.length} van {work.products.length} producten
        </span>
      </div>
      {work.error && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          <p>
            {work.error.message} <ErrorReportButton report={work.error.report} />
          </p>
          <Button variant="outline" size="sm" onClick={() => void work.reload()}>
            Opnieuw laden
          </Button>
        </div>
      )}
      {!!work.conflicts.length && (
        <p
          role="alert"
          className="mb-4 rounded border border-destructive/30 p-3 text-sm text-destructive"
        >
          Intussen gewijzigd: {work.conflicts.join(', ')}. Je lokale concept is bewaard. Verwerp het
          om met de actuele gegevens verder te werken.
        </p>
      )}
      {work.storageError && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          Dit concept kon niet in deze browser worden bewaard. Houd dit scherm open tot je de
          wijzigingen hebt gepubliceerd.
        </p>
      )}
      <div
        className={`grid items-start gap-6 ${ratesOpen ? 'lg:grid-cols-[var(--rates-sidebar-width)_minmax(0,1fr)]' : 'grid-cols-1'}`}
        style={{ '--rates-sidebar-width': `${sidebarWidth}px` } as CSSProperties}
      >
        {ratesOpen && (
          <WorkbenchRatesPanel
            rates={work.data?.rates ?? []}
            drafts={work.drafts.rates}
            busy={work.busy}
            affected={affected}
            width={sidebarWidth}
            onResize={resizeSidebar}
            onDelete={setRemovingRate}
            canDelete={!work.hasChanges && !work.busy}
            onChange={work.changeRate}
            onChangeMany={work.changeRates}
            onClose={() => setRatesOpen(false)}
          />
        )}
        <div className="min-w-0 rounded border bg-card">
          <div className="flex items-center justify-between border-b px-4 py-3 text-xs font-semibold">
            <span>Product</span>
            <span className="pr-10">Varianten en prijs</span>
          </div>
          {work.loading && !work.data ? (
            <p className="p-8 text-sm text-muted-foreground" role="status">
              Producten laden…
            </p>
          ) : !work.data && work.error ? (
            <p className="p-8 text-sm text-muted-foreground">
              De catalogus kon niet worden geladen.
            </p>
          ) : !work.products.length ? (
            <div className="space-y-3 p-8 text-center">
              <h2 className="font-semibold">Nog geen producten</h2>
              <p className="text-sm text-muted-foreground">Voeg een product toe om te beginnen.</p>
            </div>
          ) : !visible.length ? (
            <div className="space-y-3 p-8 text-center">
              <p className="text-sm text-muted-foreground">Geen producten gevonden.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setCategory('');
                }}
              >
                Filters wissen
              </Button>
            </div>
          ) : (
            groups.map(([name, products]) => (
              <div key={name}>
                <h2 className="border-b bg-muted px-4 py-2 text-xs font-semibold">{name}</h2>
                {products.map((product) => (
                  <WorkbenchProductRow
                    key={product.id}
                    product={product}
                    baseline={work.data!.products.find((item) => item.id === product.id)!}
                    onBillingPeriod={(value) => work.changeBillingPeriod(product.id, value)}
                    open={expanded.has(product.id)}
                    busy={work.busy}
                    rates={work.data!.rates}
                    ratesByKey={work.ratesByKey}
                    data={work.data!}
                    showDifferences={rateCount > 0}
                    onToggle={() =>
                      setExpanded((current) => {
                        const next = new Set(current);
                        if (next.has(product.id)) next.delete(product.id);
                        else next.add(product.id);
                        return next;
                      })
                    }
                    onChange={(spec) => work.changeSpec(product.id, spec)}
                    onStage={() => work.stageProduct(product.id)}
                    onDetail={() => void openDetail(product.id)}
                    onDuplicate={() => {
                      setDuplicateName(`${product.name} (kopie)`);
                      setDuplicatingId(product.id);
                    }}
                    hasRateChanges={rateCount > 0}
                    onPublish={() => setPublishingId(product.id)}
                    onSelectCell={setSelectedCell}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </div>
      <footer className="sticky bottom-0 z-20 mt-5 flex flex-wrap items-center justify-between gap-3 border-t bg-background py-4">
        <div className="min-w-0">
          {selectedLine &&
            selectedVariant &&
            selectedComputation &&
            expanded.has(selectedProduct!.id) && (
              <div className="mb-2 text-sm" role="status">
                <span className="font-semibold">
                  {selectedLine.description || selectedLine.code} · {selectedVariant.name}:
                </span>{' '}
                {selectedComputation.errors.some((error) => error.code === selectedLine.code)
                  ? 'Controleer de formule.'
                  : `${hours(selectedComputation.hoursByCode[selectedLine.code] ?? 0)} uur × ${money(work.ratesByKey[selectedLine.rateKey])} = ${money(selectedComputation.lineTotalCents[selectedLine.code])}`}
              </div>
            )}

          <p className="text-sm font-semibold">
            {work.hasChanges
              ? [
                  rateCount
                    ? `${rateCount} ${rateCount === 1 ? 'tarief gewijzigd' : 'tarieven gewijzigd'}`
                    : '',
                  productCount
                    ? `${productCount} ${productCount === 1 ? 'product' : 'producten'} nog niet gepubliceerd`
                    : '',
                  rateCount
                    ? `${affected.length} ${affected.length === 1 ? 'product' : 'producten'} herberekend`
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Geen wijzigingen'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {work.hasChanges
              ? 'Nieuwe offertes gebruiken de oude prijzen tot je publiceert.'
              : 'Na publiceren gelden deze prijzen voor nieuwe offertes.'}
          </p>
          {!!work.invalidVariantNames.length && (
            <p role="alert" className="text-xs text-destructive">
              Vul alle variantnamen in bij{' '}
              {work.invalidVariantNames.map((product) => product.name).join(', ')}.
            </p>
          )}
          {!!work.calculationErrors.length && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              Controleer de prijsopbouw van{' '}
              {work.calculationErrors.map((product) => product.name).join(', ')}.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {work.hasChanges && !work.storageError && (
            <span className="mr-1 flex items-center gap-1 text-xs text-muted-foreground">
              <Check className="h-3.5 w-3.5" />
              Concept bewaard in deze browser
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Ongedaan maken"
            disabled={work.busy || !work.canUndo}
            onClick={work.undo}
          >
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Opnieuw uitvoeren"
            disabled={work.busy || !work.canRedo}
            onClick={work.redo}
          >
            <Redo2 className="h-4 w-4" />
          </Button>
          {work.hasChanges && (
            <Button variant="outline" disabled={work.busy} onClick={() => setDiscardOpen(true)}>
              Verwerpen
            </Button>
          )}
          <Button
            disabled={!work.canApply}
            onClick={async () => {
              const result = await work.apply();
              if (result)
                toast.success(
                  `${result.productsUpdated} ${result.productsUpdated === 1 ? 'product' : 'producten'} en ${result.ratesUpdated} ${result.ratesUpdated === 1 ? 'tarief' : 'tarieven'} gepubliceerd`
                );
            }}
          >
            {work.busy ? 'Publiceren…' : 'Wijzigingen publiceren'}
          </Button>
        </div>
      </footer>
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuw product</DialogTitle>
          </DialogHeader>
          <label htmlFor="new-product-name" className="text-sm">
            Naam
          </label>
          <Input
            id="new-product-name"
            autoFocus
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void create();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Annuleren
            </Button>
            <Button disabled={creating || !newName.trim()} onClick={() => void create()}>
              {creating ? 'Aanmaken…' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(duplicatingProduct)}
        onOpenChange={(open) => {
          if (!open) setDuplicatingId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Product dupliceren</DialogTitle>
            <DialogDescription>
              De kopie krijgt dezelfde varianten, prijsopbouw, omschrijving, categorie en
              goedkeurders als <strong>{duplicatingProduct?.name}</strong>
              {duplicatingProduct?.edited ? ', inclusief je niet-gepubliceerde wijzigingen' : ''}.
              Hij staat als concept klaar en komt pas in offertes als je hem publiceert.
            </DialogDescription>
          </DialogHeader>
          <label htmlFor="duplicate-product-name" className="text-sm">
            Naam van de kopie
          </label>
          <Input
            id="duplicate-product-name"
            autoFocus
            value={duplicateName}
            onFocus={(event) => event.target.select()}
            onChange={(event) => setDuplicateName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void duplicate();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicatingId(null)}>
              Annuleren
            </Button>
            <Button disabled={creating || !duplicateName.trim()} onClick={() => void duplicate()}>
              {creating ? 'Dupliceren…' : 'Dupliceren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wijzigingen verwerpen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            De aanpassingen in dit werkblad worden teruggezet naar de geladen productprijzen en
            tarieven.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardOpen(false)}>
              Annuleren
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                work.discard();
                setDiscardOpen(false);
              }}
            >
              Verwerpen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {publishingProduct && (
        <PublishDialog
          productId={publishingProduct.id}
          productName={publishingProduct.name}
          open
          onOpenChange={(open) => {
            if (!open) setPublishingId(null);
          }}
          onPublish={(reason) => work.publishProduct(publishingProduct.id, reason)}
          onPublished={() => toast.success(`${publishingProduct.name} gepubliceerd`)}
        />
      )}
      <RateRemovalDialog
        rate={removingRate}
        rates={work.data?.rates ?? []}
        onClose={() => setRemovingRate(null)}
        onRemoved={work.reload}
      />
      <RatesDialog
        canDelete={!work.hasChanges && !work.busy}
        open={ratesDialog}
        onOpenChange={setRatesDialog}
        onChanged={work.reload}
      />
      <ReviewerGroupsDialog
        open={groupsOpen}
        onOpenChange={setGroupsOpen}
        onChanged={work.reload}
      />
    </div>
  );
}
